import { SharedApplicationConstants } from '@ddt-web/domain';
import * as flattenDeep from 'lodash/flattenDeep';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as uniqBy from 'lodash/uniqBy';

import { PortfolioFamily } from '../models/model-portfolio.interface';

export class ModelFamiliesUtil {

	/**
	 * Partition families into BLK models and TPM models.
	 *
	 * @param modelFamilies all model families.
	 * @return the partitioned families.
	 */
	public static getFamiliesPerCategory(modelFamilies: PortfolioFamily[]): { [categoryId: string]: PortfolioFamily[] } {
		const modelsPerCategory = {
			thirdParty: [],
			blackRock: []
		};

		if (!modelFamilies || isEmpty(modelFamilies)) {
			return modelsPerCategory;
		}

		modelFamilies.forEach(family => {
			const groups = get(family, 'groups', []);
			if (family.subType === 'TPM') {
				modelsPerCategory.thirdParty.push(family);
			} else if (!isEmpty(groups)) {
				// using only groups check for both custom models and BLK models since some BLK models need to be in custom group
				groups.forEach(group => {
					const groupId = group.id === SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP ? 'blackRock' : group.id;
					if (groupId) {
						if (groupId in modelsPerCategory) {
							modelsPerCategory[groupId].push(family);
						} else {
							modelsPerCategory[groupId] = [family];
						}
					}
				});
			} else {
				modelsPerCategory.blackRock.push(family);
			}
		});

		return modelsPerCategory;
	}

	/**
	 * Partition families into BLK, CMS and TPM models.
	 *
	 * @param modelFamilies all model families.
	 * @return the partitioned families.
	 */
	public static getFamiliesPerCategoryWithModelGrouping(modelFamilies: PortfolioFamily[]): { [categoryId: string]: PortfolioFamily[] } {
		const modelsPerCategory = {
			thirdParty: [],
			blackRock: []
		};

		if (!modelFamilies || isEmpty(modelFamilies)) {
			return modelsPerCategory;
		}

		modelFamilies.forEach(family => {
			if (family.subType === 'TPM') {
				modelsPerCategory.thirdParty.push(family);
			} else {
				const allGroups = uniqBy(flattenDeep(family.portfolios.map(portfolio => portfolio.groups || [])), 'id');
				if (isEmpty(allGroups)) {
					this.assignBlkGroup(modelsPerCategory, family);
				} else {
					allGroups.forEach(group => {
						if (group?.id === SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP) {
							this.assignBlkGroup(modelsPerCategory, family);
						} else if (group?.id) {
							if (!modelsPerCategory[group.id]) {
								modelsPerCategory[group.id] = [];
							}
							modelsPerCategory[group.id].push(Object.assign(
								{},
								family,
								{
									portfolios: family.portfolios.filter(model => {
										const modelGroups = get(model, 'groups', []);
										return modelGroups.some(modelGroup => modelGroup.id === group.id);
									}).sort((model1, model2) => Number(model1.dasModelOrder || 0) - Number(model2.dasModelOrder || 0))
								}
							));
						}
					});
				}
			}
		});

		return modelsPerCategory;
	}

	private static assignBlkGroup(modelsPerCategory: { [categoryId: string]: PortfolioFamily[] }, family: PortfolioFamily): void {
		modelsPerCategory['blackRock'].push(Object.assign(
			{},
			family,
			{
				portfolios: family.portfolios.filter(model => {
					const modelGroups = model?.groups ?? [];
					return modelGroups.some(modelGroup => modelGroup.id === SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP);
				}).sort((model1, model2) => Number(model1.dasModelOrder || 0) - Number(model2.dasModelOrder || 0))
			}
		));
	}

	/**
	 * Generate families and models list mapping per category.
	 *
	 * @param modelFamilies all model families.
	 * @return family models mapping per category.
	 */
	public static generateFamilyIdModelsMapping(modelFamilies): { [categoryId: string]: { [familyId: string]: string[] } } {

		const familyModelsPerCategory: { [categoryId: string]: { [familyId: string]: string[] } } = {};
		modelFamilies.forEach(family => {
			get(family, 'models', []).forEach(portfolio => {
				const groups = get(portfolio, 'groups', []);
				groups.forEach(group => {
					familyModelsPerCategory[group.id] = familyModelsPerCategory[group.id] || {};
					familyModelsPerCategory[group.id][family.id] = familyModelsPerCategory[group.id][family.id] || [];
					familyModelsPerCategory[group.id][family.id].push(portfolio.id);
				});
			});
		});
		return familyModelsPerCategory;
	}

	/**
	 * Get groupIds (category id) and names mapping.
	 *
	 * @param modelFamilies all model families.
	 * @return the groupIds and name mapping.
	 */
	public static getGroupIdNames(modelFamilies, excludeBlk: boolean = false, portfoliosPath = 'portfolios'): { [familyId: string]: string } {
		const groups = {};
		modelFamilies.forEach(family => {
			get(family, portfoliosPath, []).forEach(portfolio => {
				get(portfolio, 'groups', []).forEach(group => {
					if (group.id && !groups[group.id]) {
						if (!(group.id === SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP && excludeBlk)) {
							groups[group.id] = group.name;
						}
					}
				});
			});
		});
		return groups;
	}

	public static getCustomModelFamilies(modelFamilies: PortfolioFamily[]): { [familyId: string]: string } {
		const groups = {};
		modelFamilies.forEach(family => {
			get(family, 'groups', []).forEach(group => {
				if (group.id && group.id !== SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP) {
					groups[group.id] = group.name;
				}
			});
		});
		return groups;
	}
}
